import { responsiblePlhIdOfClient } from "./client-plh-lookup.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { Project, ProjectUnitArea, ProjectStatus, UnitAreaStatus } from "../../domain/project/project-domain.js";
import type { ProjectRepository } from "../../infrastructure/database/memory/in-memory-project-repository.js";
import type { AuthorizationService } from "../../permissions/authorization.js";
import type { RequestContext } from "../context/request-context.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { isPlhQualifiedProfessional } from "../../domain/identity/plh-eligibility.js";

export class ProjectService {
  constructor(
    private readonly repo: ProjectRepository,
    private readonly auth: AuthorizationService = new RbacAuthorizationService(),
    private readonly identityRepo?: any,
    private readonly audit?: any
  ) {}

  /**
   * OS CLIENTES QUE A SESSÃO PODE VER — injetado na composição a partir da
   * política oficial (`AdminService.listAuthorizedClients`).
   *
   * Todo resultado de Projeto / Unidade-Área passa a ser a INTERSEÇÃO:
   *   registros existentes ∩ Clientes autorizados ∩ clientId pedido.
   * Sem `clientId` = todos os Projetos dos Clientes AUTORIZADOS (o seletor
   * transversal de Projetos continua funcionando), nunca o banco inteiro.
   *
   * Composição sem o resolver (testes unitários antigos do serviço) mantém o
   * comportamento anterior; o servidor e o navegador sempre o injetam.
   */
  private authorizedClientIdsOf: ((context: RequestContext) => Promise<ReadonlySet<string>>) | null = null;

  public attachAuthorizedClientIdsResolver(resolver: (context: RequestContext) => Promise<ReadonlySet<string>>): void {
    this.authorizedClientIdsOf = resolver;
  }

  /** `null` = sem escopo configurado; conjunto = Clientes que a sessão enxerga. */
  private async clientScope(context: RequestContext): Promise<ReadonlySet<string> | null> {
    return this.authorizedClientIdsOf ? this.authorizedClientIdsOf(context) : null;
  }

  /** Pedido explícito de um Cliente fora do escopo: recusado, como em `listEquipments`. */
  private assertClientInScope(scope: ReadonlySet<string> | null, clientId: string): void {
    if (scope && !scope.has(String(clientId))) {
      throw new AppError(ErrorCode.Authorization, "Cross-tenant project listing denied", {
        safeMessage: "Você não possui autorização para este Cliente."
      });
    }
  }

  public async listProjects(clientId?: string, status?: ProjectStatus, context?: any): Promise<Project[]> {
    const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em createProject.
      // AUD-007: este método não tinha NENHUMA checagem de autorização e era
      // alcançável via /api/rpc sem sessão (project.read é permissão BASE —
      // ver built-in-role-policy.ts — então isto não estreita nenhum perfil
      // válido, só nega chamada sem contexto/sessão).
      this.auth.require(ctx, PermissionCode.ProjectRead);
    }
    const scope = await this.clientScope(ctx);
    const filter: { clientId?: string; status?: ProjectStatus } = {};
    if (clientId !== undefined && clientId !== null && clientId !== "") {
      this.assertClientInScope(scope, clientId);
      filter.clientId = clientId;
    }
    if (status !== undefined && status !== null) filter.status = status;
    const projects = await this.repo.listProjects(filter);
    return scope ? projects.filter(p => scope.has(String(p.clientId))) : projects;
  }

  public async getProject(id: string, context?: any): Promise<Project | null> {
    const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em listProjects (AUD-007).
      this.auth.require(ctx, PermissionCode.ProjectRead);
    }
    const project = await this.repo.getProject(id);
    if (!project) return null;
    // Projeto de Cliente fora do escopo: "não encontrado" (proteção contra IDOR).
    const scope = await this.clientScope(ctx);
    return scope && !scope.has(String(project.clientId)) ? null : project;
  }

  public async createProject(data: Partial<Project>, context?: any): Promise<Project> {
    if (this.auth) {
      // Sem contexto explícito e sem window.contextStore (caso do servidor):
      // default DENY — nunca sintetizar um contexto que já contém a própria
      // permissão sendo checada (bypass de autorização real, encontrado
      // nesta forma nos três métodos de escrita deste serviço).
      const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
      this.auth.require(ctx, PermissionCode.ProjectCreate);
    }

    if (!data.code || !data.code.trim()) {
      throw new AppError(ErrorCode.Validation, "O código do projeto é obrigatório.", {
        safeMessage: "O código do projeto é obrigatório."
      });
    }

    if (!data.name || !data.name.trim()) {
      throw new AppError(ErrorCode.Validation, "O nome do projeto é obrigatório.", {
        safeMessage: "O nome do projeto é obrigatório."
      });
    }

    if (!data.clientId || !data.clientId.trim()) {
      throw new AppError(ErrorCode.Validation, "O cliente vinculado é obrigatório.", {
        safeMessage: "O cliente vinculado é obrigatório para cadastrar um projeto."
      });
    }

    // Check unique code within client
    const existing = await this.repo.listProjects({ clientId: data.clientId });
    const normalizedCode = data.code.trim().toUpperCase();
    if (existing.some(p => p.code.trim().toUpperCase() === normalizedCode)) {
      throw new AppError(ErrorCode.Conflict, `Já existe um projeto com o código '${data.code}' para este cliente.`, {
        safeMessage: `Código '${data.code}' já está em uso neste cliente.`
      });
    }

    if (data.engineerProfessionalId && this.identityRepo) {
      let prof: any = null;
      try {
        prof = await (this.identityRepo.getProfessional ? this.identityRepo.getProfessional(data.engineerProfessionalId) : this.identityRepo.findById?.(data.engineerProfessionalId));
      } catch (e) {
        // ignora erro de fetch
      }
      
      if (!prof) {
         throw new AppError(ErrorCode.Validation, "Engenheiro/PLH não encontrado.");
      }
      if (prof.status !== 'ACTIVE') {
         throw new AppError(ErrorCode.Validation, "Engenheiro/PLH deve estar ativo.");
      }
      if (prof.professionalType !== 'ENGINEER' && !prof.functionTitle?.toLowerCase().includes('eng') && !prof.functionTitle?.toLowerCase().includes('plh')) {
         throw new AppError(ErrorCode.Validation, "O profissional não tem função compatível com Engenheiro/PLH.");
      }
      // Validar client isolation (supondo que o profissional tem um clientId de TenantOwned)
      if (prof.clientId && prof.clientId !== data.clientId) {
         throw new AppError(ErrorCode.Validation, "O profissional pertence a outro cliente.");
      }
    }

    await this.assertPlhValid(data.plhProfessionalId ?? null, data.clientId.trim());

    const now = new Date().toISOString();
    const project: Project = {
      id: `PRJ-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase(),
      code: normalizedCode,
      name: data.name.trim(),
      clientId: data.clientId.trim(),
      description: data.description ? data.description.trim() : null,
      status: data.status || 'ACTIVE',
      startDate: data.startDate || null,
      expectedEndDate: data.expectedEndDate || null,
      actualEndDate: data.actualEndDate || null,
      manager: data.manager ? data.manager.trim() : null,
      engineerProfessionalId: data.engineerProfessionalId || null,
      plhProfessionalId: data.plhProfessionalId || null,
      createdAt: now,
      updatedAt: now
    };

    await this.repo.saveProject(project);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'PROJECT', id: project.id } }));
    }

    return project;
  }

  public async updateProject(id: string, data: Partial<Project>, context?: any): Promise<Project> {
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em createProject.
      const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
      this.auth.require(ctx, PermissionCode.ProjectUpdate);
    }

    const existing = await this.repo.getProject(id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Projeto não encontrado.", {
        safeMessage: "Projeto não encontrado para atualização."
      });
    }

    if (data.code && data.code.trim()) {
      const normalizedCode = data.code.trim().toUpperCase();
      const allClientProjects = await this.repo.listProjects({ clientId: existing.clientId });
      if (allClientProjects.some(p => p.id !== id && p.code.trim().toUpperCase() === normalizedCode)) {
        throw new AppError(ErrorCode.Conflict, `Código '${data.code}' já em uso para outro projeto deste cliente.`, {
          safeMessage: `Código '${data.code}' já está em uso em outro projeto.`
        });
      }
      existing.code = normalizedCode;
    }

    if (data.name && data.name.trim()) {
      existing.name = data.name.trim();
    }

    if (data.description !== undefined) {
      existing.description = data.description ? data.description.trim() : null;
    }

    if (data.status) {
      existing.status = data.status;
    }

    if (data.startDate !== undefined) {
      existing.startDate = data.startDate || null;
    }

    if (data.expectedEndDate !== undefined) {
      existing.expectedEndDate = data.expectedEndDate || null;
    }

    if (data.actualEndDate !== undefined) {
      existing.actualEndDate = data.actualEndDate || null;
    }

    if (data.manager !== undefined) {
      existing.manager = data.manager ? data.manager.trim() : null;
    }

    if (data.engineerProfessionalId !== undefined && data.engineerProfessionalId !== existing.engineerProfessionalId) {
      if (data.engineerProfessionalId && this.identityRepo) {
        let prof: any = null;
        try {
          prof = await (this.identityRepo.getProfessional ? this.identityRepo.getProfessional(data.engineerProfessionalId) : this.identityRepo.findById?.(data.engineerProfessionalId));
        } catch (e) {}
        
        if (!prof) {
           throw new AppError(ErrorCode.Validation, "Engenheiro/PLH não encontrado.");
        }
        if (prof.status !== 'ACTIVE') {
           throw new AppError(ErrorCode.Validation, "Engenheiro/PLH deve estar ativo.");
        }
        if (prof.professionalType !== 'ENGINEER' && !prof.functionTitle?.toLowerCase().includes('eng') && !prof.functionTitle?.toLowerCase().includes('plh')) {
           throw new AppError(ErrorCode.Validation, "O profissional não tem função compatível com Engenheiro/PLH.");
        }
        if (prof.clientId && prof.clientId !== existing.clientId) {
           throw new AppError(ErrorCode.Validation, "O profissional pertence a outro cliente.");
        }
      }

      const previousEngineerId = existing.engineerProfessionalId;
      existing.engineerProfessionalId = data.engineerProfessionalId || null;

      if (this.audit) {
        try {
          const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
          await this.audit.record(ctx, {
            action: 'UPDATE_PROJECT_ENGINEER', // Using string to bypass strict enum if missing
            entityType: "Project",
            entityId: existing.id,
            previousState: { engineerProfessionalId: previousEngineerId },
            newState: { engineerProfessionalId: existing.engineerProfessionalId },
            metadata: {
              projectId: existing.id,
              previousEngineerId,
              newEngineerId: existing.engineerProfessionalId
            }
          });
        } catch (e) {
          // ignora erro de auditoria para não quebrar fluxo principal
        }
      }
    }

    if (data.plhProfessionalId !== undefined && (data.plhProfessionalId || null) !== (existing.plhProfessionalId || null)) {
      await this.assertPlhValid(data.plhProfessionalId || null, existing.clientId);
      const previousPlhId = existing.plhProfessionalId || null;
      existing.plhProfessionalId = data.plhProfessionalId || null;
      if (this.audit) {
        try {
          const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
          await this.audit.record(ctx, {
            action: 'UPDATE_PROJECT_PLH',
            entityType: "Project",
            entityId: existing.id,
            previousState: { plhProfessionalId: previousPlhId },
            newState: { plhProfessionalId: existing.plhProfessionalId },
            metadata: { projectId: existing.id, previousPlhId, newPlhId: existing.plhProfessionalId }
          });
        } catch (e) {
          // ignora erro de auditoria para não quebrar fluxo principal
        }
      }
    }

    existing.updatedAt = new Date().toISOString();
    await this.repo.saveProject(existing);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'PROJECT', id: existing.id } }));
    }

    return existing;
  }

  /** PLH RESPONSÁVEL do Projeto: só um Profissional habilitado pelo gate oficial para o Cliente do Projeto. */
  private async assertPlhValid(plhProfessionalId: string | null, clientId: string): Promise<void> {
    if (!plhProfessionalId || !this.identityRepo) return;
    let prof: any = null;
    try {
      prof = await (this.identityRepo.getProfessional ? this.identityRepo.getProfessional(plhProfessionalId) : this.identityRepo.findById?.(plhProfessionalId));
    } catch (e) { /* tratado abaixo */ }
    if (!prof) {
      throw new AppError(ErrorCode.Validation, "PLH não encontrado.", { safeMessage: "O PLH responsável selecionado não foi encontrado no cadastro de Profissionais." });
    }
    const gate = isPlhQualifiedProfessional(prof, await responsiblePlhIdOfClient(this.identityRepo as never, String(clientId)));
    if (!gate.allowed) {
      throw new AppError(ErrorCode.Validation, "PLH not qualified", { safeMessage: `O Profissional selecionado não está habilitado como PLH para este Cliente: ${gate.reason || ""}` });
    }
  }

  public async deleteProject(id: string, context?: any): Promise<void> {
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em createProject.
      const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
      this.auth.require(ctx, PermissionCode.ProjectDelete);
    }

    const existing = await this.repo.getProject(id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Projeto não encontrado.");
    }
    return this.repo.deleteProject(id);
  }

  // --- UNIT / AREA OPERATIONS ---

  public async listUnitAreas(filterOrProjectId?: string | { projectId?: string; clientId?: string; status?: UnitAreaStatus }, clientId?: string, context?: any): Promise<ProjectUnitArea[]> {
    const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em listProjects (AUD-007).
      // unit_area.read é permissão BASE (built-in-role-policy.ts), então isto
      // não estreita nenhum perfil válido — só nega chamada sem contexto/sessão.
      this.auth.require(ctx, PermissionCode.UnitAreaRead);
    }
    let filter: { projectId?: string; clientId?: string; status?: UnitAreaStatus };
    if (typeof filterOrProjectId === 'object' && filterOrProjectId !== null) {
      filter = filterOrProjectId;
    } else {
      filter = {};
      if (filterOrProjectId !== undefined && filterOrProjectId !== null) filter.projectId = filterOrProjectId;
      if (clientId !== undefined && clientId !== null) filter.clientId = clientId;
    }
    // Mesma interseção dos Projetos: Unidade/Área só dos Clientes autorizados.
    const scope = await this.clientScope(ctx);
    if (filter.clientId) this.assertClientInScope(scope, filter.clientId);
    const areas = await this.repo.listUnitAreas(filter);
    return scope ? areas.filter(a => scope.has(String(a.clientId))) : areas;
  }

  public async getUnitArea(id: string, context?: any): Promise<ProjectUnitArea | null> {
    const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em listProjects (AUD-007).
      this.auth.require(ctx, PermissionCode.UnitAreaRead);
    }
    const unitArea = await this.repo.getUnitArea(id);
    if (!unitArea) return null;
    const scope = await this.clientScope(ctx);
    return scope && !scope.has(String(unitArea.clientId)) ? null : unitArea;
  }

  public async createUnitArea(data: Partial<ProjectUnitArea>, context?: any): Promise<ProjectUnitArea> {
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em createProject.
      const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
      this.auth.require(ctx, PermissionCode.UnitAreaCreate);
    }

    if (!data.code || !data.code.trim()) {
      throw new AppError(ErrorCode.Validation, "O código da unidade/área é obrigatório.", {
        safeMessage: "O código da unidade/área é obrigatório."
      });
    }

    if (!data.name || !data.name.trim()) {
      throw new AppError(ErrorCode.Validation, "O nome da unidade/área é obrigatório.", {
        safeMessage: "O nome da unidade/área é obrigatório."
      });
    }

    if (!data.projectId || !data.projectId.trim()) {
      throw new AppError(ErrorCode.Validation, "O projeto vinculado é obrigatório.", {
        safeMessage: "O projeto vinculado é obrigatório para cadastrar a unidade/área."
      });
    }

    const project = await this.repo.getProject(data.projectId);
    if (!project) {
      throw new AppError(ErrorCode.NotFound, "Projeto vinculado não foi encontrado.", {
        safeMessage: "Projeto vinculado não existe."
      });
    }

    const existingUnits = await this.repo.listUnitAreas({ projectId: data.projectId });
    const normalizedCode = data.code.trim().toUpperCase();
    if (existingUnits.some(u => u.code.trim().toUpperCase() === normalizedCode)) {
      throw new AppError(ErrorCode.Conflict, `Código '${data.code}' já em uso neste projeto.`, {
        safeMessage: `Código '${data.code}' já está em uso neste projeto.`
      });
    }

    const now = new Date().toISOString();
    const unitArea: ProjectUnitArea = {
      id: `UA-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase(),
      code: normalizedCode,
      name: data.name.trim(),
      projectId: project.id,
      clientId: project.clientId, // herdado do projeto
      description: data.description ? data.description.trim() : null,
      status: data.status || 'ACTIVE',
      createdAt: now,
      updatedAt: now
    };

    await this.repo.saveUnitArea(unitArea);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'UNIT_AREA', id: unitArea.id } }));
    }

    return unitArea;
  }

  public async updateUnitArea(id: string, data: Partial<ProjectUnitArea>, context?: any): Promise<ProjectUnitArea> {
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em createProject.
      const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
      this.auth.require(ctx, PermissionCode.UnitAreaUpdate);
    }

    const existing = await this.repo.getUnitArea(id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Unidade/Área não encontrada.");
    }

    if (data.code && data.code.trim()) {
      const normalizedCode = data.code.trim().toUpperCase();
      const units = await this.repo.listUnitAreas({ projectId: existing.projectId });
      if (units.some(u => u.id !== id && u.code.trim().toUpperCase() === normalizedCode)) {
        throw new AppError(ErrorCode.Conflict, `Código '${data.code}' já em uso neste projeto.`);
      }
      existing.code = normalizedCode;
    }

    if (data.name && data.name.trim()) {
      existing.name = data.name.trim();
    }

    if (data.description !== undefined) {
      existing.description = data.description ? data.description.trim() : null;
    }

    if (data.status) {
      existing.status = data.status;
    }

    if (data.projectId && data.projectId.trim() && data.projectId !== existing.projectId) {
      const targetProject = await this.repo.getProject(data.projectId);
      if (!targetProject) {
        throw new AppError(ErrorCode.NotFound, "Projeto de destino não encontrado.");
      }
      existing.projectId = targetProject.id;
      existing.clientId = targetProject.clientId;
      existing.isPendingProjectBinding = false;
    }

    existing.updatedAt = new Date().toISOString();
    await this.repo.saveUnitArea(existing);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'UNIT_AREA', id: existing.id } }));
    }

    return existing;
  }

  public async deleteUnitArea(id: string, context?: any): Promise<void> {
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em createProject.
      const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
      this.auth.require(ctx, PermissionCode.UnitAreaDelete);
    }
    return this.repo.deleteUnitArea(id);
  }

  public async listPendingUnitAreas(clientId?: string, context?: any): Promise<ProjectUnitArea[]> {
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em listProjects (AUD-007).
      const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
      this.auth.require(ctx, PermissionCode.UnitAreaRead);
    }
    return this.repo.listPendingUnitAreas(clientId);
  }

  public async bindUnitAreaToProject(unitAreaId: string, projectId: string, context?: any): Promise<ProjectUnitArea> {
    if (this.auth) {
      // Default DENY sem contexto — ver comentário equivalente em createProject.
      const ctx = context || (typeof window !== "undefined" && (window as any).contextStore ? (window as any).contextStore.get() : {});
      this.auth.require(ctx, PermissionCode.UnitAreaUpdate);
    }
    const project = await this.repo.getProject(projectId);
    if (!project) {
      throw new AppError(ErrorCode.NotFound, "Projeto não encontrado.");
    }
    const unit = await this.repo.bindUnitAreaToProject(unitAreaId, project.id, project.clientId);
    if (!unit) {
      throw new AppError(ErrorCode.NotFound, "Unidade/Área não encontrada.");
    }
    return unit;
  }

  public async toggleProjectStatus(id: string, context?: any): Promise<Project> {
    const project = await this.repo.getProject(id);
    if (!project) {
      throw new AppError(ErrorCode.NotFound, "Projeto não encontrado.");
    }
    const newStatus: ProjectStatus = project.status === 'ACTIVE' ? 'CANCELLED' : 'ACTIVE';
    return this.updateProject(id, { status: newStatus }, context);
  }

  public async toggleUnitAreaStatus(id: string, context?: any): Promise<ProjectUnitArea> {
    const unit = await this.repo.getUnitArea(id);
    if (!unit) {
      throw new AppError(ErrorCode.NotFound, "Unidade/Área não encontrada.");
    }
    const newStatus: UnitAreaStatus = unit.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    return this.updateUnitArea(id, { status: newStatus }, context);
  }
}
